/**
 * Atualização do PWA do CRM — como a versão nova chega ao navegador.
 *
 * Doc: docs/funcionalidades/atualizacao-do-crm-pwa.md (inclui o incidente de 12/10/2026).
 *
 * O MODELO (decidido em 12/10/2026):
 *  1. O sw.js novo se ativa sozinho (`skipWaiting` + `clientsClaim`, ver vite.config.ts).
 *     Não existe mais estado "waiting": a versão nova assume o controle assim que instala.
 *  2. A PÁGINA aberta continua rodando o JS antigo até recarregar. Recarregar à força no
 *     meio de um formulário perde trabalho, então quando o controle muda o app só marca
 *     `recarregarPendente` e mostra o aviso persistente "Nova versão disponível — Atualizar".
 *  3. Recarrega sozinho só nos momentos em que não há trabalho a perder:
 *     - troca de rota na SPA (a tela anterior já foi desmontada pelo próprio router);
 *     - aba oculta, sem interação há `ociosidadeMs` e sem diálogo/sheet aberto;
 *     - falha ao carregar um chunk preguiçoso (`vite:preloadError`): o chunk antigo saiu do
 *       servidor e do precache — sem recarregar, a tela quebraria de qualquer forma.
 *  4. Procura versão nova a cada `intervaloMs` e sempre que a aba volta a ficar visível
 *     (o navegador sozinho só checa em navegação, e uma aba de recepção fica aberta o dia todo).
 *
 * Este módulo não importa React nem `virtual:pwa-register` — recebe `registerSW` e o
 * ambiente por parâmetro, para ser testado em jsdom sem service worker.
 */
import type { RegisterSWOptions } from "vite-plugin-pwa/types";

export type RegisterSW = (options: RegisterSWOptions) => (reloadPage?: boolean) => Promise<void>;

export interface EstadoPwa {
  /** O SW novo já controla a página; falta recarregar para rodar o JS novo. */
  recarregarPendente: boolean;
  /** Primeira instalação concluída (app pronto para abrir offline). */
  prontoOffline: boolean;
}

export interface OpcoesPwa {
  /** Intervalo de checagem de versão nova (padrão 30 min). */
  intervaloMs?: number;
  /** Ociosidade mínima para recarregar sozinho com a aba oculta (padrão 30 min). */
  ociosidadeMs?: number;
  /** No máximo UMA recarga por chunk perdido nesta janela (padrão 5 min). */
  janelaAntiLacoMs?: number;
  win?: Window;
  doc?: Document;
  agora?: () => number;
  recarregar?: () => void;
}

export interface AtualizadorPwa {
  getSnapshot: () => EstadoPwa;
  subscribe: (ouvinte: () => void) => () => void;
  /** Botão "Atualizar" do aviso. */
  atualizarAgora: () => void;
  /** Chamado pelo watcher a cada troca de rota da SPA. */
  aoTrocarDeRota: () => void;
  /**
   * Predicado "há escrita em andamento" (o watcher liga `queryClient.isMutating() > 0`).
   * Enquanto true, nenhuma recarga automática acontece — nem na troca de rota, nem com a
   * aba oculta. O clique em "Atualizar" é decisão do usuário e não é bloqueado.
   */
  definirEscritaPendente: (predicado: (() => boolean) | null) => void;
  /** Para testes: remove timers e listeners. */
  encerrar: () => void;
}

export const INTERVALO_PADRAO_MS = 30 * 60 * 1000;
export const OCIOSIDADE_PADRAO_MS = 30 * 60 * 1000;
export const JANELA_ANTI_LACO_PADRAO_MS = 5 * 60 * 1000;
const CHAVE_ANTI_LACO = "pps-crm:reload-por-chunk";
export const CACHE_DE_API_ANTIGO = "supabase-cache";

const EVENTOS_DE_INTERACAO = ["pointerdown", "keydown", "input", "wheel", "touchstart"] as const;

export function criarAtualizadorPwa(registerSW: RegisterSW, opcoes: OpcoesPwa = {}): AtualizadorPwa {
  const win = opcoes.win ?? window;
  const doc = opcoes.doc ?? document;
  const agora = opcoes.agora ?? (() => Date.now());
  const recarregar = opcoes.recarregar ?? (() => win.location.reload());
  const intervaloMs = opcoes.intervaloMs ?? INTERVALO_PADRAO_MS;
  const ociosidadeMs = opcoes.ociosidadeMs ?? OCIOSIDADE_PADRAO_MS;
  const janelaAntiLacoMs = opcoes.janelaAntiLacoMs ?? JANELA_ANTI_LACO_PADRAO_MS;

  let estado: EstadoPwa = { recarregarPendente: false, prontoOffline: false };
  const ouvintes = new Set<() => void>();
  const limpezas: Array<() => void> = [];
  let ultimaInteracao = agora();
  let timerOcioso: ReturnType<typeof setTimeout> | null = null;
  let jaRecarregando = false;
  let escritaPendente: (() => boolean) | null = null;
  const haEscritaPendente = () => {
    try {
      return escritaPendente?.() ?? false;
    } catch {
      return true; // na dúvida, não recarrega
    }
  };

  const definir = (parcial: Partial<EstadoPwa>) => {
    estado = { ...estado, ...parcial };
    ouvintes.forEach((o) => o());
  };

  const escutar = <K extends string>(alvo: EventTarget, evento: K, fn: EventListener, opts?: AddEventListenerOptions) => {
    alvo.addEventListener(evento, fn, opts);
    limpezas.push(() => alvo.removeEventListener(evento, fn, opts));
  };

  const recarregarUmaVez = () => {
    if (jaRecarregando) return;
    jaRecarregando = true;
    recarregar();
  };

  const haDialogoAberto = () =>
    doc.querySelector('[role="dialog"], [role="alertdialog"]') !== null;

  /** Aba oculta, ociosa e sem diálogo aberto: ninguém está no meio de nada. */
  const tentarRecarregarEmSegundoPlano = () => {
    if (!estado.recarregarPendente || doc.visibilityState !== "hidden") return;
    const ocioso = agora() - ultimaInteracao;
    if (ocioso >= ociosidadeMs && !haDialogoAberto() && !haEscritaPendente()) {
      recarregarUmaVez();
      return;
    }
    // Ainda não ocioso o bastante: reavalia quando completar a janela, se seguir oculta.
    if (timerOcioso) clearTimeout(timerOcioso);
    timerOcioso = setTimeout(tentarRecarregarEmSegundoPlano, Math.max(ociosidadeMs - ocioso, 1000));
  };

  for (const ev of EVENTOS_DE_INTERACAO) {
    escutar(win, ev, () => {
      ultimaInteracao = agora();
    }, { passive: true, capture: true });
  }

  let registro: ServiceWorkerRegistration | undefined;
  const procurarVersaoNova = () => {
    if (!registro || win.navigator.onLine === false) return;
    // `update()` rejeita offline/servidor fora — não é erro de app, só tenta de novo depois.
    registro.update().catch(() => undefined);
  };

  escutar(doc, "visibilitychange", () => {
    if (doc.visibilityState === "visible") {
      if (timerOcioso) clearTimeout(timerOcioso);
      procurarVersaoNova();
    } else {
      tentarRecarregarEmSegundoPlano();
    }
  });

  // Chunk preguiçoso que sumiu (deploy novo apagou o hash antigo do servidor e o SW novo
  // tirou o arquivo do precache). A navegação já ia descartar a tela: recarregar é seguro.
  // Anti-laço: no máximo UMA recarga por esse motivo a cada `janelaAntiLacoMs` (5 min),
  // contada em sessionStorage — sobrevive à recarga, então não depende de quanto a página
  // demora para subir. Limite atingido, ou storage ilegível: o erro segue para o
  // ErrorBoundary (falha fechado — sem contador confiável não há como evitar o laço).
  escutar(win, "vite:preloadError", (evento) => {
    if (win.navigator.onLine === false) return;
    try {
      const ultimo = Number(win.sessionStorage.getItem(CHAVE_ANTI_LACO) ?? 0);
      if (Number.isFinite(ultimo) && ultimo > 0 && agora() - ultimo < janelaAntiLacoMs) return;
      win.sessionStorage.setItem(CHAVE_ANTI_LACO, String(agora()));
    } catch {
      return;
    }
    evento.preventDefault();
    recarregarUmaVez();
  });

  // Cache de API da regra antiga (NetworkFirst em *.supabase.co, removida em 12/10/2026).
  // O SW novo não a usa mais, mas a Cache Storage continuaria guardando respostas
  // autenticadas no navegador — apaga.
  win.caches?.delete(CACHE_DE_API_ANTIGO).catch(() => undefined);

  registerSW({
    // Com skipWaiting o SW novo nunca fica "waiting": o sinal é o controle ter mudado.
    // O plugin chamaria window.location.reload() aqui; trocamos pelo aviso.
    onNeedReload() {
      definir({ recarregarPendente: true });
      tentarRecarregarEmSegundoPlano();
    },
    onOfflineReady() {
      definir({ prontoOffline: true });
    },
    onRegisteredSW(_url, r) {
      registro = r;
      if (!r) return;
      const id = setInterval(procurarVersaoNova, intervaloMs);
      limpezas.push(() => clearInterval(id));
    },
    onRegisterError() {
      /* sem SW o app funciona normalmente (só sem offline); nada a avisar */
    },
  });

  return {
    getSnapshot: () => estado,
    subscribe(ouvinte) {
      ouvintes.add(ouvinte);
      return () => ouvintes.delete(ouvinte);
    },
    atualizarAgora: recarregarUmaVez,
    aoTrocarDeRota() {
      // Escrita em andamento: adia — a próxima troca de rota sem mutation pendente recarrega.
      if (estado.recarregarPendente && !haEscritaPendente()) recarregarUmaVez();
    },
    definirEscritaPendente(predicado) {
      escritaPendente = predicado;
    },
    encerrar() {
      if (timerOcioso) clearTimeout(timerOcioso);
      limpezas.splice(0).forEach((f) => f());
      ouvintes.clear();
    },
  };
}

let atualizadorGlobal: AtualizadorPwa | null = null;

/** main.tsx chama uma vez (fora do Capacitor nativo). */
export function iniciarAtualizacaoDoPwa(registerSW: RegisterSW, opcoes?: OpcoesPwa): AtualizadorPwa {
  atualizadorGlobal ??= criarAtualizadorPwa(registerSW, opcoes);
  return atualizadorGlobal;
}

/** null quando não há SW (Capacitor nativo, dev, navegador sem suporte). */
export function obterAtualizadorPwa(): AtualizadorPwa | null {
  return atualizadorGlobal;
}

/** Só para testes. */
export function __reiniciarAtualizadorPwa() {
  atualizadorGlobal?.encerrar();
  atualizadorGlobal = null;
}
